<template>
  <div
    v-bind="$attrs"
    class="novel-content-wrap"
    :class="{
      [themeTemplate?.styleClass ?? '']: true,
      justify: themeTemplate?.justify,
      bold: themeTemplate?.bold,
    }"
    ref="container"
    @copy.prevent
  >
    <template v-if="loadingAbove">
      <Skeleton class="h-20 shadow-none" />
      <Skeleton class="h-20 shadow-none" />
    </template>
    <NovelRenderComponent
      v-for="block in augmentedText"
      :textBlock="block"
      :showComments="showCommentIcons"
      :showBookmarks="showBookmarkIcons"
      :language
      :bookmarked="block.paragraphId && paragraphBookmarkMap[block.paragraphId]"
      :overrideFont="fontFamily !== 'inherit'"
      :ref="(el) => registerParagraphRef(block.paragraphId, el)"
      :comments="
        block.paragraphId
          ? (paragraphStats?.[block.paragraphId]?.commentCount ?? 0)
          : undefined
      "
      @comment="(v) => emit('comment', v)"
      @footnote="(v) => emit('footnote', v)"
    />
    <template v-if="loadingBelow">
      <Skeleton class="h-20 shadow-none" />
      <Skeleton class="h-20 shadow-none" />
    </template>
    <slot name="after"> </slot>
  </div>
</template>

<script setup lang="ts">
import {
  augmentWithFootnoteIds,
  getContent,
  type TextBlock,
} from "~/src/novel/core";
import {
  FontSheet,
  getFontFamily,
  type NovelStyleTemplate,
} from "~/src/novel/style";
import {
  type AvailableLanguages,
  isNovelParagraph,
  type NovelBookmarkEntity,
} from "~/src/api";

const { text, language, themeTemplate, margin, chapterBookmarks } =
  defineProps<{
    text: TextBlock[];
    language?: AvailableLanguages;
    themeTemplate?: NovelStyleTemplate;
    showCommentIcons?: boolean;
    showBookmarkIcons?: boolean;

    margin?: number;
    paragraphStats?: Record<string, { commentCount: number }>;
    chapterBookmarks?: NovelBookmarkEntity[];

    loadingAbove?: boolean;
    loadingBelow?: boolean;
  }>();

const augmentedText = computed(() => augmentWithFootnoteIds(text));

const emit = defineEmits<{
  (e: "comment", id?: string): void;
  (e: "footnote", id: string): void;
}>();

const paragraphs = ref<Record<string, ComponentPublicInstance>>({});
const registerParagraphRef = (
  id: string | undefined | null,
  el: Element | ComponentPublicInstance | null,
) => {
  if (!id || !el) return;
  if (el instanceof Element) return;
  paragraphs.value[id] = el;
};

const container = ref<HTMLDivElement>();

const paragraphBookmarkMap = computed(() => {
  if (!chapterBookmarks) return {};
  return Object.fromEntries(
    chapterBookmarks
      .map((bm) => {
        if (bm.attributes.position !== "0;0") return;
        const relPara = bm.relationships.find(isNovelParagraph)?.id;
        return [relPara, bm];
      })
      .filter(onlyTruthys),
  );
});

const fontFamily = computed(() => themeTemplate?.fontFamily ?? "inherit");
const fontRatio = computed(() => themeTemplate?.fontRatio ?? 1);
const lineHeight = computed(() => themeTemplate?.lineHeight ?? 1.5);
const letterSpacing = computed(() => themeTemplate?.letterSpacing ?? 0);
const wordSpacing = computed(() => themeTemplate?.wordSpacing ?? 0);
const textMargin = computed(() => (margin ?? 32) + "px");

const fontMeta = computed(
  () => FontSheet[fontFamily.value.split('"').filter(onlyTruthys)[0]],
);
const baseFontRatio = computed(() => fontMeta.value?.baseFontRatio ?? 1);
const realFontSize = computed(() => fontRatio.value - 1 + baseFontRatio.value);

function getFonts(block: TextBlock, fonts = new Set<string>()): Set<string> {
  const mapped = getFontFamily(block.fontAppearance, language);
  if (mapped) fonts.add(mapped.value);
  const content = getContent(block);
  if (typeof content === "string") return fonts;
  content.forEach((b) => getFonts(b, fonts));
  return fonts;
}

const fonts = computed(() => {
  const uniqueFonts = new Set<string>();
  text.forEach((block) => getFonts(block, uniqueFonts));
  return Array.from(uniqueFonts)
    .map((font) => FontSheet[font]?.link)
    .filter(onlyTruthys);
});

useHead({
  link: () =>
    fonts.value
      .map((link) => ({
        href: link,
        rel: "stylesheet",
      }))
      .concat(
        fontMeta.value?.link
          ? [
              {
                href: fontMeta.value.link,
                rel: "stylesheet",
              },
            ]
          : [],
      ),
});
let tmFn: (() => void) | null = null;
let bmTimeout: NodeJS.Timeout;

defineExpose({
  scrollIntoView(
    paragraphId: string,
    highlight?: boolean,
    options?: ScrollIntoViewOptions,
  ) {
    const para = document.getElementById(paragraphId);
    para?.scrollIntoView({
      // behavior: "smooth",
      block: "center",
      ...options,
    });

    if (!highlight) return;

    if (bmTimeout) {
      clearTimeout(bmTimeout);
      tmFn?.();
    }
    para?.setAttribute("hl", "");

    tmFn = () => para?.removeAttribute("hl");
    bmTimeout = setTimeout(tmFn, 1000);
  },
  paragraphs,
});
</script>

<style lang="postcss">
@import url("assets/css/novel/style.pcss");
@import url("assets/css/novel/format.pcss");
@import url("assets/css/novel/themes.pcss");

.novel-content-wrap {
  --text-font-ratio: 1;
  --novel-font-size: calc(v-bind(realFontSize) * 1em);
  font-size: var(--novel-font-size);
  line-height: calc(v-bind(lineHeight) * 1em);
  word-spacing: calc(1em / 16 * v-bind(wordSpacing));
  letter-spacing: calc(1em / 16 * v-bind(letterSpacing));
  font-family: v-bind(fontFamily);
  padding-left: v-bind(textMargin);
  padding-right: v-bind(textMargin);
  @apply space-y-4 sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg min-h-full;

  &.override {
    font-family: v-bind(fontFamily) !important;
  }

  &.justify {
    @apply text-justify;
  }

  &.bold {
    @apply font-bold;
  }
}
</style>
